import Link from 'next/link';
import ShopifySEO from '../components/shopify/ShopifySEO';
import ShopifyTool from '../components/shopify/ShopifyTool';
import {
  standalonePage,
  standalonePages,
  standalonePath
} from '../content/shopify/standalone';
import { loadMessages } from '../utils/i18n';

const supportedLocales = ['en', 'pl', 'uk'];

export default function StandaloneShopifyResource({ page, locale, related }) {
  const path = standalonePath(page.slug, locale);
  const alternatePaths = Object.fromEntries(
    supportedLocales.map((item) => [item, standalonePath(page.slug, item)])
  );
  const { labels } = page;
  return (
    <div lang={locale} className="bg-slate-50 text-slate-900">
      <ShopifySEO
        title={page.title}
        description={page.description}
        path={path}
        article
        locale={locale}
        alternatePaths={alternatePaths}
        breadcrumb={{
          home: labels.home,
          hub: labels.hub,
          hubPath:
            locale === 'en' ? '/shopify-guides' : `/${locale}/shopify-guides`
        }}
      />
      <header className="border-b border-blue-100 bg-gradient-to-br from-white via-blue-50 to-indigo-50 px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-8 flex flex-wrap gap-2 text-sm text-slate-600"
          >
            <Link href="/" locale={locale} className="underline">
              {labels.home}
            </Link>
            <span aria-hidden="true">/</span>
            <Link
              href={
                locale === 'en'
                  ? '/shopify-guides'
                  : `/${locale}/shopify-guides`
              }
              locale={false}
              className="underline"
            >
              {labels.hub}
            </Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">{page.heading}</span>
          </nav>
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-blue-700">
            {labels.category}
          </p>
          <h1 className="max-w-4xl text-4xl font-bold leading-tight md:text-6xl">
            {page.heading}
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-700">
            {page.intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href="#planner"
              className="rounded-xl bg-blue-700 px-6 py-3 font-semibold text-white shadow-sm hover:bg-blue-800"
            >
              {labels.tool}
            </a>
            <a
              href="https://apps.shopify.com/afivio"
              className="rounded-xl border border-slate-300 bg-white px-6 py-3 font-semibold hover:border-blue-600"
            >
              {labels.app}
            </a>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_280px]">
          <article>
            <section id="planner" className="mb-12 scroll-mt-24">
              <ShopifyTool
                key={`${locale}-${page.slug}`}
                tool={page.tool}
                locale={locale}
              />
            </section>
            <div className="space-y-12">
              {page.sections.map((section) => (
                <section key={section.heading}>
                  <h2 className="mb-5 text-2xl font-bold">{section.heading}</h2>
                  {section.paragraphs.map((paragraph) => (
                    <p
                      key={paragraph}
                      className="mb-4 leading-8 text-slate-700"
                    >
                      {paragraph}
                    </p>
                  ))}
                </section>
              ))}
            </div>
            <section className="my-12 rounded-2xl border border-indigo-100 bg-indigo-50 p-7">
              <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-indigo-700">
                {labels.example}
              </p>
              <h2 className="mb-4 text-2xl font-bold">{page.example.title}</h2>
              <p className="leading-8 text-slate-700">{page.example.text}</p>
            </section>
            <section className="mb-12">
              <h2 className="mb-5 text-2xl font-bold">{labels.faq}</h2>
              <div className="divide-y divide-slate-200">
                {page.faqs.map((faq) => (
                  <details key={faq.question} className="py-5">
                    <summary className="cursor-pointer font-semibold">
                      {faq.question}
                    </summary>
                    <p className="mt-4 leading-8 text-slate-700">
                      {faq.answer}
                    </p>
                  </details>
                ))}
              </div>
            </section>
            <section className="rounded-2xl bg-slate-900 p-8 text-white">
              <h2 className="text-2xl font-bold">{labels.plan}</h2>
              <p className="my-5 leading-7 text-slate-300">{labels.note}</p>
              <Link
                href="/shopify-integration"
                locale={locale}
                className="font-semibold underline"
              >
                {labels.integration}
              </Link>
            </section>
          </article>
          <aside>
            <section className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="mb-4 text-lg font-bold">{labels.related}</h2>
              <ul className="space-y-4">
                {related.map((item) => (
                  <li key={item.slug}>
                    <Link
                      href={standalonePath(item.slug, locale)}
                      locale={false}
                      className="text-sm font-semibold text-blue-700 underline underline-offset-4"
                    >
                      {item.heading}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          </aside>
        </div>
      </main>
    </div>
  );
}

export function getStaticPaths() {
  return {
    paths: standalonePages.flatMap(({ slug }) =>
      supportedLocales.map((locale) => ({
        params: { shopifyResource: slug },
        locale
      }))
    ),
    fallback: false
  };
}

export async function getStaticProps({ params, locale = 'en' }) {
  const page = standalonePage(params.shopifyResource, locale);
  if (!page || !supportedLocales.includes(locale)) return { notFound: true };
  const related = standalonePages
    .filter((item) => item.slug !== page.slug)
    .slice(0, 3)
    .map((item) => standalonePage(item.slug, locale));
  return {
    props: {
      page,
      locale,
      related,
      messages: await loadMessages(locale, {
        includeShopifyStandalone: true,
        includeShopifyGuides: true
      })
    }
  };
}
